import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { MemberSelect } from '@/features/members';
import { fromLocalInput, toLocalInput } from '@/lib/datetime';
import { INTERVIEW_OUTCOMES, INTERVIEW_STAGES } from '../constants';
import { CriteriaRatingsField } from '../components/CriteriaRatingsField';
import {
  useCandidates,
  useCreateInterview,
  useInterview,
  useUpdateInterview,
} from '../queries';
import type { Candidate, Interview, InterviewInput } from '../types';
import { candidateName } from '../types';

const INTERVIEWS_PATH = '/hrm/recruitment/interviews';

const schema = z.object({
  candidate: z.string().min(1, 'Candidate is required'),
  stage_name: z.string().trim().min(1, 'Stage is required').max(150),
  scheduled_at: z.string(),
  interviewer: z.string(),
  outcome: z.enum(['pending', 'passed', 'failed']),
  notes: z.string(),
  criteria_ratings: z.array(
    z.object({ criterion: z.string(), score: z.number().int().min(1).max(5) }),
  ),
});

type FormValues = z.infer<typeof schema>;

/**
 * Create + edit share this page. The data the selects need (the interview in
 * edit mode + the candidate options) is loaded by the outer component, which
 * holds the form until everything is ready — so the form initialises ONCE from
 * `defaultValues` with the option lists present, avoiding empty/stale prefills.
 */
export function NewInterviewPage() {
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const { data: existing, isPending: loadingExisting } = useInterview(editId ?? '');
  const { data: candidatesData } = useCandidates();

  // Edit mode needs the interview + candidate options before mounting the form.
  if (isEdit && (loadingExisting || !existing || !candidatesData)) {
    return (
      <div className="w-full max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <InterviewFormFields
      editId={editId}
      existing={existing ?? null}
      candidates={candidatesData?.results ?? []}
    />
  );
}

function InterviewFormFields({
  editId,
  existing,
  candidates,
}: {
  editId?: string;
  existing: Interview | null;
  candidates: Candidate[];
}) {
  const navigate = useNavigate();
  const isEdit = Boolean(editId);
  const create = useCreateInterview();
  const update = useUpdateInterview();

  // Stage is free text; if the saved value isn't a quick-pick, include it.
  const stageOptions = useMemo(() => {
    const base = [...INTERVIEW_STAGES] as string[];
    if (existing?.stage_name && !base.includes(existing.stage_name)) {
      base.unshift(existing.stage_name);
    }
    return base;
  }, [existing]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: existing
      ? {
          candidate: String(existing.candidate),
          stage_name: existing.stage_name,
          scheduled_at: toLocalInput(existing.scheduled_at),
          interviewer: existing.interviewer ? String(existing.interviewer) : '',
          outcome: existing.outcome,
          notes: existing.notes,
          criteria_ratings: existing.criteria_ratings ?? [],
        }
      : {
          candidate: '', stage_name: '', scheduled_at: '', interviewer: '',
          outcome: 'pending', notes: '', criteria_ratings: [],
        },
  });

  const backTo = isEdit ? `${INTERVIEWS_PATH}/${editId}` : INTERVIEWS_PATH;

  async function handleSubmit(values: FormValues) {
    const input: InterviewInput = {
      candidate: Number(values.candidate),
      stage_name: values.stage_name,
      scheduled_at: fromLocalInput(values.scheduled_at),
      interviewer: values.interviewer ? Number(values.interviewer) : null,
      outcome: values.outcome,
      notes: values.notes,
      criteria_ratings: values.criteria_ratings,
    };
    try {
      if (isEdit && editId) {
        await update.mutateAsync({ id: editId, input });
      } else {
        await create.mutateAsync(input);
      }
      navigate(backTo);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          form.setError(field as keyof FormValues, { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <div className="w-full max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(backTo)}>
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit interview' : 'Schedule interview'}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <FormSection variant="card" title="Interview">
            <FormField
              control={form.control}
              name="candidate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Candidate</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a candidate" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {candidates.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {candidateName(c)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="stage_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stage</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a stage" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {stageOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="interviewer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Interviewer</FormLabel>
                  <FormControl>
                    <MemberSelect
                      queryKey="interview-interviewer-picker"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Unassigned"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection variant="card" title="When">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="scheduled_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>When</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="outcome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Outcome</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {INTERVIEW_OUTCOMES.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>

          <FormSection
            variant="card"
            title="Scorecard"
            description="Rate each criterion 1–5 (leave blank to skip)."
          >
            <FormField
              control={form.control}
              name="criteria_ratings"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CriteriaRatingsField value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection variant="card" title="Notes">
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <MarkdownEditor
                      value={field.value}
                      onChange={field.onChange}
                      rows={4}
                      placeholder="Agenda, panel, focus areas…"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <div className="flex gap-2">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Schedule interview'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(backTo)}>
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
